import {
  useRef,
  useEffect,
  cloneElement,
  createContext,
  useContext,
  forwardRef,
  useState,
  Children,
  isValidElement,
} from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ListSubheader from "@material-ui/core/ListSubheader";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { VariableSizeList } from "react-window";
import { untrim } from "../utils/utils";
import clsx from "clsx";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef(function OuterElementType(props, ref) {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = child => {
    if (isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const useStyles = makeStyles(theme => ({
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
      lineHeight: "1rem",
    },
  },
  formControl: {
    margin: theme.spacing(1),
  },
}));

//Pass the list of violations to as OPTIONS
// const OPTIONS = Array.from(new Array(10000))
//   .map(() => random(10 + Math.ceil(Math.random() * 20)))
//   .sort((a, b) => a.toUpperCase().localeCompare(b.toUpperCase()));

const renderGroup = params => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

const VirtualizedAutocomplete = ({
  title = "",
  options = [],
  loading = true,
  error = "",
  value = {},
  updateFields = () => alert("No updateFields function passed"),
  stateName = "",
  oriId = null,
  disabled = false,
  customClass = "",
}) => {
  const classes = useStyles();

  const [autocompleteState, setAutocompleteState] = useState({
    stateOptions: [],
    stateValue: value,
  });

  useEffect(() => {
    if (options) {
      setAutocompleteState(prevState => {
        return {
          ...prevState,
          stateOptions: options
            .filter(
              o => (o?.oriId === oriId || o?.oriId === null) && !o.inactive
            )
            .sort((a, b) => (b?.position ?? 0) - (a?.position ?? 0)),
        };
      });
    }
  }, [options, oriId]);

  return options ? (
    <Autocomplete
      disabled={loading || error || disabled ? true : false}
      id={`virtualized-${title}`}
      fullWidth
      autoHighlight
      // autoSelect
      disableListWrap
      className={clsx(classes.formControl, customClass)}
      ListboxComponent={ListboxComponent}
      renderGroup={renderGroup}
      options={autocompleteState.stateOptions}
      getOptionSelected={(option, value) => {
        return option.id === value.id ? option : value;
      }}
      getOptionDisabled={option => option?.inactive}
      value={value}
      // groupBy={(option) => option[0].toUpperCase()}
      renderInput={params => (
        <TextField
          {...params}
          label={title}
          InputLabelProps={{ shrink: true }}
        />
      )}
      // renderOption={(option) => <Typography noWrap>{option.name}</Typography>}
      getOptionLabel={option => {
        if (option.fullName === null || option.fullName === undefined) {
          return "";
        } else {
          return (
            untrim(option.fullName) +
              untrim(option?.statute) +
              untrim(option?.section) +
              (option.inactive ? " (INACTIVE)" : "") +
              (option?.yoe ? " (YOE)" : "") || ""
          );
        }
      }}
      onChange={(event, value) => updateFields(value, stateName)}
    />
  ) : (
    <p>Loading Violations</p>
  );
};

VirtualizedAutocomplete.propTypes = {
  title: PropTypes.string,
  options: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  value: PropTypes.object,
  updateFields: PropTypes.func,
  stateName: PropTypes.string,
  oriId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  customClass: PropTypes.string,
};

export default VirtualizedAutocomplete;
